import styled from 'styled-components';
import BottomModal from '../../components/modal/BottomModal';
import { useMapFilterContext } from '../../../context/MapFilterContext';
import SwitchButton from '../../components/button/SwitchButton';
import SegmentedToggle from '../../components/button/SegmentedToggle';
import { useState } from 'react';

interface Props {
  onClose: () => void;
}

function MapSettingModal({ onClose }: Props) {
  const {
    mapType,
    setMapType,
    setOverlayType,
    overlayType,
    isPyeonPrice,
    isTotalPrice,
    setIsTotalPrice,
    setIsPyeonPrice,
    onPublicPrice,
    setOnPublicPrice,
  } = useMapFilterContext();

  const [unit, setUnit] = useState(isPyeonPrice ? '평' : 'm');
  const [price, setPrice] = useState(isTotalPrice ? '총액' : '단가');

  const handleClickReset = () => {
    setMapType('ROADMAP');
    setOverlayType('');
    setOnPublicPrice(false);
    setUnit('m');
    setIsPyeonPrice(false);
    setPrice('총액');
    setIsTotalPrice(true);
  };

  return (
    <BottomModal onClose={onClose}>
      <Header>
        <Title>지도 설정</Title>
        <CloseButton
          src="/assets/images/close/close-black.svg"
          alt="close"
          onClick={onClose}
        />
      </Header>
      <Inner>
        <MapTypeSection>
          <MapTypeButton
            $isActive={mapType === 'ROADMAP'}
            onClick={() => {
              setMapType('ROADMAP');
            }}
          >
            <MapTypeIconWrapper $isActive={mapType === 'ROADMAP'}>
              <NormalIcon active={mapType === 'ROADMAP'} />
            </MapTypeIconWrapper>
            일반도
          </MapTypeButton>
          <MapTypeButton
            $isActive={mapType === 'HYBRID'}
            onClick={() => {
              setMapType('HYBRID');
            }}
          >
            <MapTypeIconWrapper $isActive={mapType === 'HYBRID'}>
              <SatelliteIcon active={mapType === 'HYBRID'} />
            </MapTypeIconWrapper>
            위성도
          </MapTypeButton>
          <MapTypeButton
            $isActive={overlayType === 'USE_DISTRICT'}
            onClick={() => {
              overlayType === 'USE_DISTRICT'
                ? setOverlayType('')
                : setOverlayType('USE_DISTRICT');
            }}
          >
            <MapTypeIconWrapper $isActive={overlayType === 'USE_DISTRICT'}>
              <CadastralIcon active={overlayType === 'USE_DISTRICT'} />
            </MapTypeIconWrapper>
            지적도
          </MapTypeButton>
        </MapTypeSection>

        <PublicPriceSection>
          <PublicPriceHeader>
            <PublicPriceTitle>공시지가</PublicPriceTitle>
            <SwitchButton
              isActive={onPublicPrice}
              handlePushToggle={() => {
                setOnPublicPrice((prev) => !prev);
              }}
            />
          </PublicPriceHeader>
          <PublicPriceDesc>필지 기준 공시지가 차이 (%)</PublicPriceDesc>
          <img src="assets/images/map/pricePercentChart.svg" alt="공시지가" />
          <AmenityPercentBox>
            <AmenityPercent>-10%~</AmenityPercent>
            <AmenityPercent>-5%~</AmenityPercent>
            <AmenityPercent>-1%~</AmenityPercent>
            <AmenityPercent>동일</AmenityPercent>
            <AmenityPercent>+1%</AmenityPercent>
            <AmenityPercent>+5%</AmenityPercent>
            <AmenityPercent>+10%</AmenityPercent>
          </AmenityPercentBox>
        </PublicPriceSection>

        <ToggleSection>
          <RowContent>
            <Label>면적</Label>

            <SegmentedToggle
              ariaLabel="면적 단위 선택"
              value={unit}
              onChange={(v: string) => {
                setUnit(v);
                setIsPyeonPrice(v === '평');
              }}
              width={88}
              options={[
                { value: '평', label: '평' },
                {
                  value: 'm',
                  label: '㎡',
                },
              ]}
            />
          </RowContent>
          <RowContent>
            <Label>가격</Label>
            <SegmentedToggle
              ariaLabel="면적 단위 선택"
              value={price}
              onChange={(v: string) => {
                setPrice(v);
                setIsTotalPrice(v === '총액');
              }}
              width={88}
              options={[
                { value: '총액', label: '총액' },
                {
                  value: '단가',
                  label: '단가',
                },
              ]}
            />
          </RowContent>
        </ToggleSection>
      </Inner>
      <ButtonGroup>
        <ResetButton
          onClick={() => {
            handleClickReset();
          }}
        >
          초기화
        </ResetButton>
        <ApplyButton
          onClick={() => {
            onClose();
          }}
        >
          적용
        </ApplyButton>
      </ButtonGroup>
    </BottomModal>
  );
}

export default MapSettingModal;

const Header = styled.div`
  display: flex;
  height: 56px;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  position: relative;
`;

const Title = styled.div`
  flex: 1 0 0;
  color: #222;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;

const CloseButton = styled.img`
  position: absolute;
  right: 16px;
  top: 17px;
  cursor: pointer;
`;

const Inner = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const MapTypeSection = styled.div`
  display: flex;
  padding: 8px 0;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const MapTypeButton = styled.div<{ $isActive?: boolean }>`
  display: flex;
  padding: 0 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;

  color: ${({ $isActive }) => ($isActive ? '#222' : '#37383C9C')};
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const MapTypeIconWrapper = styled.div<{ $isActive?: boolean }>`
  display: flex;
  padding: 8px;
  align-items: center;
  border-radius: 20px;
  background: ${({ $isActive }) => ($isActive ? '#008AFF' : '#0220470D')};
`;

const PublicPriceSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  & > img {
    width: 100%;
  }
`;

const PublicPriceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const PublicPriceTitle = styled.div`
  flex: 1 0 0;
  color: #222;
  font-size: 16px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const PublicPriceDesc = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 400;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const AmenityPercentBox = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

const AmenityPercent = styled.div`
  flex: 1 0 0;
  color: #222;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const ToggleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const RowContent = styled.div`
  display: flex;
  padding: 8px 0;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const Label = styled.div`
  flex: 1 0 0;
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const ButtonGroup = styled.div`
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Button = styled.button`
  border: none;
  outline: none;

  display: flex;
  padding: 12px 28px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;

  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const ResetButton = styled(Button)`
  color: #616161;
  background: rgba(112, 115, 124, 0.08);
`;

const ApplyButton = styled(Button)`
  flex: 1 0 0;
  background: #008aff;
  color: #fff;
`;

interface IconProp {
  active: boolean;
}

const NormalIcon = ({ active }: IconProp) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
    >
      <g clipPath="url(#clip0_3075_12461)">
        <path
          d="M6.5026 15.8333L3.47876 17.0429C2.93138 17.2618 2.33594 16.8587 2.33594 16.2691V4.73086C2.33594 4.39011 2.5434 4.08368 2.85978 3.95713L6.5026 2.5V15.8333Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          d="M13.1693 4.16667V17.5L8.16927 15.8333V2.5L13.1693 4.16667Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          d="M19.0026 15.2691C19.0026 15.6099 18.7951 15.9163 18.4788 16.0429L14.8359 17.5V4.16667L17.8598 2.95713C18.4072 2.73818 19.0026 3.14131 19.0026 3.73086V15.2691Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
      </g>
      <defs>
        <clipPath id="clip0_3075_12461">
          <rect
            width="20"
            height="20"
            fill="white"
            transform="translate(0.667969)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const SatelliteIcon = ({ active }: IconProp) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_3075_11323)">
        <path
          d="M17.4792 10.635C17.9514 10.6351 18.3343 11.0181 18.3345 11.4903C18.3345 15.2694 15.2704 18.3335 11.4912 18.3335C11.0191 18.3334 10.6361 17.9504 10.6359 17.4782C10.6359 17.0059 11.019 16.6231 11.4912 16.6229C14.3256 16.6229 16.6239 14.3246 16.6239 11.4903C16.6241 11.018 17.0069 10.635 17.4792 10.635Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          d="M14.058 10.635C14.5301 10.6352 14.9131 11.0182 14.9133 11.4903C14.9133 13.3798 13.3808 14.9123 11.4912 14.9123C11.0191 14.9121 10.6362 14.5291 10.6359 14.057C10.6359 13.5847 11.019 13.2011 11.4912 13.2009C12.436 13.2008 13.2018 12.435 13.2019 11.4903C13.202 11.018 13.5857 10.635 14.058 10.635Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          fillrulefill-rule="evenodd"
          clipRule="evenodd"
          d="M10.3853 1.91752C10.7193 1.5835 11.2614 1.58353 11.5954 1.91752L14.014 4.33614C14.348 4.67014 14.348 5.21141 14.014 5.54545L12.2001 7.36104C12.5338 7.69506 12.5339 8.23638 12.2001 8.57035L10.9908 9.77966L11.5954 10.3843C11.9294 10.7183 11.9294 11.2604 11.5954 11.5944C11.2614 11.9284 10.7193 11.9285 10.3853 11.5944L9.78064 10.9898L8.57133 12.1991C8.23736 12.533 7.69603 12.5329 7.36202 12.1991L5.54642 14.0131C5.21238 14.347 4.67112 14.3471 4.33711 14.0131L1.91849 11.5944C1.58451 11.2604 1.58448 10.7183 1.91849 10.3843L3.73246 8.57035L1.91849 6.75638C1.58446 6.42235 1.58446 5.87948 1.91849 5.54545L5.54642 1.91752C5.88046 1.58348 6.42333 1.58348 6.75736 1.91752L8.57133 3.73148L10.3853 1.91752ZM3.73246 6.15092L7.96667 10.3843L10.3853 7.96569L6.15189 3.73148L3.73246 6.15092Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
      </g>
      <defs>
        <clipPath id="clip0_3075_11323">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const CadastralIcon = ({ active }: IconProp) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
    >
      <g clipPath="url(#clip0_3075_12469)">
        <path
          d="M7.00391 8.33317C7.92438 8.33317 8.67057 9.07936 8.67057 9.99984V16.6665C8.67057 17.587 7.92438 18.3332 7.00391 18.3332H3.67057C2.7501 18.3332 2.00391 17.587 2.00391 16.6665V9.99984C2.00391 9.07936 2.7501 8.33317 3.67057 8.33317H7.00391Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          d="M17.0039 8.33317C17.9244 8.33317 18.6706 9.07936 18.6706 9.99984V16.6665C18.6706 17.587 17.9244 18.3332 17.0039 18.3332H12.0039C11.0834 18.3332 10.3372 17.587 10.3372 16.6665V9.99984C10.3372 9.07936 11.0834 8.33317 12.0039 8.33317H17.0039Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          d="M10.3372 1.6665C11.2577 1.6665 12.0039 2.4127 12.0039 3.33317V4.99984C12.0039 5.92031 11.2577 6.6665 10.3372 6.6665H3.67057C2.7501 6.6665 2.00391 5.92031 2.00391 4.99984V3.33317C2.00391 2.4127 2.7501 1.6665 3.67057 1.6665H10.3372Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
        <path
          d="M17.0039 1.6665C17.9244 1.6665 18.6706 2.4127 18.6706 3.33317V4.99984C18.6706 5.92031 17.9244 6.6665 17.0039 6.6665H15.3372C14.4168 6.6665 13.6706 5.92031 13.6706 4.99984V3.33317C13.6706 2.4127 14.4168 1.6665 15.3372 1.6665H17.0039Z"
          fill={active ? 'white' : '#022047'}
          fillOpacity={active ? '1' : '0.31'}
        />
      </g>
      <defs>
        <clipPath id="clip0_3075_12469">
          <rect
            width="20"
            height="20"
            fill="white"
            transform="translate(0.335938)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
